import type { UrlLocation } from '@seek/chalice-types';
import { metrics } from '@seek/metrics-js';
import { enrichLocation, isHomepagePath } from '@seek/seek-jobs-seo';
import omit from 'lodash/omit';
import { stringify as buildQueryString } from 'query-string';
import type { MouseEvent } from 'react';
import { type NavigateFunction, useNavigate } from 'react-router';

import { logger } from 'src/modules/logger';
import { shouldOpenLinkExternally } from 'src/modules/navlink-rules';
import { timeoutPromise, tryPromise } from 'src/modules/promise';
import { getQualifiedLocation } from 'src/modules/qualified-location';
import runHooks from 'src/modules/redux-hooks';

import type { TypedAction, TypedThunkAction } from '../types';

import {
  CAMPAIGN_LINK_CLICKED,
  LOCATION_CHANGED,
  LOCATION_HASH_CHANGED,
  ADD_TO_QUERY,
  REMOVE_FROM_QUERY,
  CLEAR_PAGE_QUERY,
  type LocationState,
  SET_REQUEST_ID,
  type SetRequestIdAction,
} from './types';

export const initialState: LocationState = {
  url: null,
  prevPathname: null,
  query: null,
  pageNumber: undefined,
  isHomepage: true,
  pathname: '',
  hostname: '',
  href: '',
  port: '',
  protocol: '',
  requestId: undefined,
};

const updateQuerystring = (
  originalUrl: string | null,
  query: Record<string, any>,
) => {
  const urlSearchParams = new URLSearchParams(query);
  const url = new URL(originalUrl ?? '');
  url.search = urlSearchParams.toString();

  return url.toString();
};

export default function reducer(
  state: LocationState = initialState,
  action: TypedAction,
): LocationState {
  switch (action.type) {
    case LOCATION_CHANGED: {
      const { url, location } = action.payload;
      const { pathname, query, hash, port: actionPort } = location;
      const {
        hostname,
        href,
        port: statePort,
        protocol,
        pathname: prevPathname,
      } = state;

      const enrichedLocation = enrichLocation(
        {
          hostname,
          href,
          port: statePort || actionPort,
          protocol,
          pathname,
          query,
          hash,
        },
        url,
      );

      return {
        ...state,
        url,
        prevPathname: prevPathname !== pathname ? prevPathname : null,
        pageNumber: parseInt(query?.page || '1', 10),
        isHomepage: isHomepagePath(enrichedLocation.pathname!),
        ...enrichedLocation,
      };
    }

    case LOCATION_HASH_CHANGED: {
      const { hash } = action.payload;
      return {
        ...state,
        hash,
      };
    }

    case ADD_TO_QUERY: {
      const { query: fragment } = action.payload;
      const query = { ...state.query, ...fragment };

      const url = updateQuerystring(state.url, query);
      return { ...state, url, query };
    }

    case REMOVE_FROM_QUERY: {
      const query = { ...state.query };
      action.payload.keys.forEach((key) => delete query[key]);

      const url = updateQuerystring(state.url, query);

      return {
        ...state,
        url,
        query,
      };
    }

    case SET_REQUEST_ID: {
      const { requestId } = action.payload;
      return {
        ...state,
        requestId,
      };
    }

    case CLEAR_PAGE_QUERY: {
      return {
        ...state,
        query: omit(state.query, ['page']),
        pageNumber: undefined,
      };
    }

    default:
      return state;
  }
}

export function setRequestId(requestId?: string): SetRequestIdAction {
  return {
    type: SET_REQUEST_ID,
    payload: {
      requestId,
    },
  };
}

export function locationChanged(
  url: string,
  location: UrlLocation,
): TypedAction {
  return {
    type: LOCATION_CHANGED,
    payload: {
      url,
      location,
    },
  };
}

export function locationHashChanged(hash: string): TypedAction {
  return {
    type: LOCATION_HASH_CHANGED,
    payload: {
      hash,
    },
  };
}

export function addToQuery(query: any): TypedAction {
  return {
    type: ADD_TO_QUERY,
    payload: {
      query,
    },
  };
}

export function removeFromQuery(keys: [string]): TypedAction {
  return {
    type: REMOVE_FROM_QUERY,
    payload: {
      keys,
    },
  };
}

export function campaignLinkClicked({
  linkName,
  trackingCode,
}: {
  linkName: string;
  trackingCode: string;
}): TypedAction {
  return {
    type: CAMPAIGN_LINK_CLICKED,
    payload: {
      linkName,
      trackingCode,
    },
  };
}

export function clearPageQuery(): TypedAction {
  return {
    type: CLEAR_PAGE_QUERY,
  };
}

const NavType = {
  NEW_WINDOW: 'NEW_WINDOW', // link opened in new window
  OUTSIDE_APP: 'OUTSIDE_APP', // link external to SPA (same window)
  INSIDE_APP: 'INSIDE_APP', // link internal to SPA
};

export const pushNavigate = ({
  state,
  location,
  navigate,
}: {
  state?: any;
  location: any;
  navigate: NavigateFunction;
}) => {
  try {
    /**
     * Double encodes the dangling percent characters in the pathname.
     *
     * We want to preserve the literal '%25' in the URL as displayed
     * in the browser's address bar, we need to counteract this automatic decoding. We do this by
     * double-encoding the '%25' sequence itself. Here's how it works:
     * - First, we split the pathname on '%25'. This separates the string into parts around each occurrence of '%25'.
     * - We then join these parts with '%2525'. The '%25' within '%2525' is the encoded version of '%',
     *   so '%2525' is effectively the double-encoded version of '%25'.
     * - As a result, when the browser processes this double-encoded sequence ('%2525'), it decodes it once to '%25',
     *   thus preserving the appearance of the original '%25' sequence in the URL.
     *
     * This technique is crucial for URLs containing literal '%25' sequences that we need to display as-is in the URL bar,
     * considering the browser's automatic decoding behavior.
     */
    const doubleEncodePathname = (pathname: string) =>
      pathname.split('%25').join('%2525');

    /* Fix me: This is a temporary fix to prevent the history.push from being called before the UI updates
    as this negatively impacts the INP score of the page. */
    const PERFORMANCE_IMPROVEMENT_DELAY = 10;

    const pushToHistory = () => {
      const updatedLocation = {
        ...location,
        pathname: doubleEncodePathname(location.pathname),
        search: location.search || buildQueryString(location.query),
      };

      navigate(updatedLocation, {
        // Only homepage currently uses ScrollRestoration
        // We're excluding any other page
        preventScrollReset: !isHomepagePath(location.pathname),
        state,
      });
    };

    setTimeout(pushToHistory, PERFORMANCE_IMPROVEMENT_DELAY);
  } catch (error) {
    logger.error(error as Error, 'Error calling history.push');
  }
};

const doNavigate = (
  location: UrlLocation,
  target: string | undefined,
  navType: string,
  navigate: NavigateFunction,
) => {
  if (ENV.CLIENT) {
    if (navType === NavType.INSIDE_APP) {
      pushNavigate({ location, navigate });
    } else {
      window.open(location.href, target);
    }
  }
  return Promise.resolve();
};

const MAX_DELAY = 1000;
const ANALYTICS_DELAY = 300;

type LinkNavigateProps = {
  event: MouseEvent;
  target?: string;
  location: any;
  preNavigationHooks?: any[];
  navigate: NavigateFunction;
};

export const useLinkNavigate = () => {
  const navigate = useNavigate();
  return ({
    event,
    location,
    preNavigationHooks,
    target,
  }: Omit<LinkNavigateProps, 'navigate'>) =>
    linkNavigate({
      event,
      location,
      navigate,
      preNavigationHooks,
      target,
    });
};

function linkNavigate({
  event,
  target,
  location: nextLocation,
  preNavigationHooks,
  navigate,
}: LinkNavigateProps): TypedThunkAction {
  return (dispatch, getState) => {
    const {
      location: currentLocation,
      appConfig: { site },
    } = getState();
    const location = getQualifiedLocation(currentLocation, nextLocation, site);
    const isExternalLink = shouldOpenLinkExternally(
      location,
      currentLocation,
      site,
    );

    // upon rejection, hold errors for throwing later
    let firstError: any = null;
    const holdFirstError = (err: any) => {
      logger.error(err);
      firstError = firstError || (err instanceof Error ? err : null);
    };

    const navType =
      (target === '_blank' && NavType.NEW_WINDOW) ||
      (isExternalLink && NavType.OUTSIDE_APP) ||
      NavType.INSIDE_APP;

    const navTasks = [];

    // if new window navigate as early as possible & without delay to prevent
    // browser/ad-blockers preventing navigate (particularly firefox)
    if (navType === NavType.NEW_WINDOW) {
      doNavigate(location, target, navType, navigate);
    }

    // pre-navigation hooks
    if (preNavigationHooks?.length) {
      navTasks.push(() =>
        runHooks(dispatch, preNavigationHooks).catch(holdFirstError),
      );
    }

    // internal (SPA) navigation
    if (navType === NavType.INSIDE_APP) {
      event.preventDefault();
      navTasks.push(() => doNavigate(location, target, navType, navigate));
    }

    // external (outside SPA) navigation - delay navigation to ensure analytics time to send
    if (navType === NavType.OUTSIDE_APP) {
      navTasks.push(() =>
        timeoutPromise(
          tryPromise(() => Promise.resolve()),
          MAX_DELAY,
        )
          .then(
            (result) =>
              new Promise((resolve) => {
                setTimeout(() => resolve(result), ANALYTICS_DELAY);
              }),
          )

          .catch((e) => {
            if (e.isTimedOut) {
              // max delay time has passed, continue to navigation
              metrics.count('navigate-outside-app-analytics-delay.timeout', [
                'timeout:true',
              ]);
            }
            return e;
          })
          .catch(holdFirstError)
          .then(() => doNavigate(location, target, navType, navigate)),
      );
    }

    const allTasks = navTasks.reduce(
      (promiseChain, task): Promise<any> => promiseChain.then(task),
      Promise.resolve(),
    );

    // run nav tasks in sequence
    return allTasks.catch(holdFirstError).then(() => {
      // this `then` ensures we do not return the last reduced value which makes testing more predictable
      if (firstError !== null) {
        logger.error(firstError, 'firstError');
      }
    });
  };
}
